import React from "react";
import { t } from "ttag";

import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    IVoucher,
    IVoucherSearchErrors,
    IVoucherSearchResults,
} from "../models";
import { deleteVoucherCode, searchVoucherCode } from "../thunks";

const isSearchErrors = (
    obj: IVoucherSearchResults | IVoucherSearchErrors,
): obj is IVoucherSearchErrors => {
    return (obj as IVoucherSearchErrors).detail !== undefined;
};

const handleSearch = async (
    codeRef: React.RefObject<HTMLInputElement | null>,
    dispatch: ReturnType<typeof useAppDispatch>,
    setIsLoading: (loading: boolean) => void,
) => {
    if (!codeRef.current) {
        return;
    }

    setIsLoading(true);
    try {
        await dispatch(searchVoucherCode(codeRef.current.value));
    } finally {
        setIsLoading(false);
    }
};

const handleDelete = async (
    voucher: IVoucher,
    dispatch: ReturnType<typeof useAppDispatch>,
    setIsLoading: (loading: boolean) => void,
    codeRef: React.RefObject<HTMLInputElement | null>,
) => {
    setIsLoading(true);
    try {
        const deleteAction = await dispatch(deleteVoucherCode(voucher));
        if (deleteVoucherCode.fulfilled.match(deleteAction)) {
            const codeToRefresh = codeRef.current?.value || voucher.code;
            await dispatch(searchVoucherCode(codeToRefresh));
        }
    } finally {
        setIsLoading(false);
    }
};

export const Vouchers = (): React.ReactNode => {
    const dispatch = useAppDispatch();
    const vouchers = useAppSelector((state) => state.csr.voucherSearch);
    const [isLoading, setIsLoading] = React.useState(false);
    const codeRef = React.useRef<HTMLInputElement>(null);

    const onSearch = async (event: React.FormEvent<HTMLElement>) => {
        event.preventDefault();
        await handleSearch(codeRef, dispatch, setIsLoading);
    };

    const onDelete = async (
        event: React.FormEvent<HTMLElement>,
        voucher: IVoucher,
    ) => {
        event.preventDefault();
        await handleDelete(voucher, dispatch, setIsLoading, codeRef);
    };

    const [searchResults, error] = isSearchErrors(vouchers)
        ? [null, vouchers]
        : [vouchers, null];
    const voucher =
        searchResults && searchResults.results.length > 0
            ? searchResults.results[0]
            : null;

    return (
        <div className="csr-vouchers">
            <form onSubmit={onSearch}>
                <div className="csr-vouchers__input">
                    <input
                        type="text"
                        ref={codeRef}
                        placeholder={t`Enter a voucher code to lookup.`}
                        required={true}
                    />
                </div>
                <div className="csr-vouchers__button">
                    <button type="submit" disabled={isLoading}>
                        {t`Search`}
                    </button>
                </div>
            </form>

            {error && <p className="csr-vouchers__error">{error.detail}</p>}
            {voucher && (
                <div className="csr-vouchers__results">
                    <h3>{t`Voucher Details`}</h3>
                    <table>
                        <tbody>
                            <tr>
                                <th scope="row">{t`Name`}</th>
                                <td>{voucher.name}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t`Code`}</th>
                                <td>{voucher.code}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t`Valid Dates`}</th>
                                <td>
                                    {new Date(
                                        voucher.start_datetime,
                                    ).toLocaleString()}
                                    &nbsp;&ndash;&nbsp;
                                    {new Date(
                                        voucher.end_datetime,
                                    ).toLocaleString()}
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">{t`Num. Baskets`}</th>
                                <td>{voucher.num_basket_additions}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t`Num. Orders`}</th>
                                <td>{voucher.num_orders}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t`Num. Children`}</th>
                                <td>{voucher.num_children}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="button-group">
                        <button
                            className="danger"
                            type="button"
                            disabled={isLoading || voucher.num_children > 0}
                            onClick={(event) => {
                                void onDelete(event, voucher);
                            }}
                        >
                            {t`Delete Voucher`}
                        </button>
                        <p className="button-group__help">
                            {voucher.num_children > 0
                                ? t`This voucher can not be deleted since it has child codes.`
                                : t`Delete this voucher code. This can not be undone.`}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
